import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { optimizeImage } from "../../lib/optimizeImage";

const ease = [0.65, 0, 0.35, 1];
const srcOf = (img) => (typeof img === "string" ? img : img.src);

const useTrackSize = (ref) => {
    const [size, setSize] = useState({ w: 0, h: 0, gap: 0 });
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const update = () => {
            const w = el.clientWidth;
            const h = w < 640 ? w * 0.48 : w < 1024 ? Math.min(w * 0.5, window.innerHeight * 0.6) : Math.min(w * 0.4, window.innerHeight * 0.72);
            setSize({ w, h: Math.round(h), gap: w < 640 ? 14 : w < 1024 ? 28 : 48 });
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref]);
    return size;
};

const useAspects = (urls) => {
    const [aspects, setAspects] = useState(() => urls.map(() => 16 / 10));
    useEffect(() => {
        let alive = true;
        urls.forEach((u, i) => {
            const im = new Image();
            im.onload = () =>
                alive &&
                setAspects((a) => {
                    const next = [...a];
                    next[i] = im.naturalWidth / im.naturalHeight;
                    return next;
                });
            im.src = u;
        });
        return () => {
            alive = false;
        };
    }, [urls.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
    return aspects;
};

const ArrowBtn = ({ onClick, disabled, label, children, testid }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        data-testid={testid}
        className="group inline-flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-full border border-[rgba(45,42,38,0.2)] text-text-primary transition-[background-color,color,border-color,opacity] duration-300 hover:bg-[#2D2A26] hover:text-cream hover:border-[#2D2A26] focus:outline-none focus-visible:ring-2 focus-visible:ring-coral disabled:opacity-30 disabled:pointer-events-none"
    >
        {children}
    </button>
);

const Controls = ({ index, total, width, go, testid }) => (
    <div className="mx-auto mt-8 md:mt-10 flex items-center justify-between gap-6" style={{ width }}>
        <div className="flex items-center gap-4" data-testid={`${testid}-counter`}>
            <span className="font-heading text-lg md:text-xl font-medium tabular-nums text-text-primary">
                {String(index + 1).padStart(2, "0")}
            </span>
            <span className="relative h-px w-12 md:w-24 bg-[rgba(45,42,38,0.15)] overflow-hidden">
                <motion.span
                    className="absolute inset-y-0 left-0 bg-[#2D2A26]"
                    animate={{ width: `${((index + 1) / total) * 100}%` }}
                    transition={{ duration: 0.9, ease }}
                />
            </span>
            <span className="font-body text-sm md:text-base tabular-nums text-text-secondary">
                {String(total).padStart(2, "0")}
            </span>
        </div>
        <div className="flex items-center gap-3">
            <ArrowBtn onClick={() => go(index - 1)} disabled={index === 0} label="Previous image" testid={`${testid}-prev`}>
                <ArrowLeft className="h-4 w-4 md:h-5 md:w-5 transition-transform duration-300 group-hover:-translate-x-0.5" strokeWidth={1.5} />
            </ArrowBtn>
            <ArrowBtn onClick={() => go(index + 1)} disabled={index === total - 1} label="Next image" testid={`${testid}-next`}>
                <ArrowRight className="h-4 w-4 md:h-5 md:w-5 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.5} />
            </ArrowBtn>
        </div>
    </div>
);

export const ProjectCarousel = ({ images, title, testid = "project-carousel" }) => {
    const ref = useRef(null);
    const { w, h, gap } = useTrackSize(ref);
    const urls = images.map((img) => optimizeImage(srcOf(img), 1600));
    const aspects = useAspects(urls);
    const [index, setIndex] = useState(0);
    const last = images.length - 1;

    const go = useCallback((i) => setIndex(Math.max(0, Math.min(last, i))), [last]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") go(index + 1);
            if (e.key === "ArrowLeft") go(index - 1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [go, index]);

    const widths = aspects.map((a) => Math.round(h * a));
    const before = widths.slice(0, index).reduce((s, v) => s + v + gap, 0);
    const x = w / 2 - before - (widths[index] || 0) / 2;

    return (
        <section className="pt-4 md:pt-8 pb-16 md:pb-28" data-testid={testid} aria-roledescription="carousel">
            <div ref={ref} className="relative w-full overflow-hidden" style={{ height: h || "40vw" }}>
                <motion.div
                    className="flex h-full cursor-grab active:cursor-grabbing"
                    style={{ gap }}
                    initial={false}
                    animate={{ x }}
                    transition={{ duration: 0.9, ease }}
                    drag={images.length > 1 ? "x" : false}
                    dragConstraints={{ left: x, right: x }}
                    dragElastic={0.15}
                    onDragEnd={(_, info) => {
                        if (info.offset.x < -60 || info.velocity.x < -400) go(index + 1);
                        else if (info.offset.x > 60 || info.velocity.x > 400) go(index - 1);
                    }}
                >
                    {urls.map((src, i) => {
                        const active = i === index;
                        return (
                            <motion.button
                                type="button"
                                key={src + i}
                                onClick={() => go(i)}
                                data-testid={`${testid}-slide-${i}`}
                                aria-label={`Image ${i + 1} of ${images.length}`}
                                aria-current={active}
                                animate={{ opacity: active ? 1 : 0.4 }}
                                transition={{ duration: 0.9, ease }}
                                className="relative h-full shrink-0 overflow-hidden rounded-[18px] md:rounded-[26px] select-none"
                                style={{ width: widths[i] }}
                            >
                                <img
                                    src={src}
                                    alt={`${title}, image ${i + 1}`}
                                    draggable={false}
                                    loading={i < 3 ? "eager" : "lazy"}
                                    decoding="async"
                                    className="block h-full w-auto max-w-none pointer-events-none"
                                />
                            </motion.button>
                        );
                    })}
                </motion.div>
            </div>

            {images.length > 1 && (
                <Controls index={index} total={images.length} width={w < 640 ? w - 48 : Math.min(w * 0.68, 1200)} go={go} testid={testid} />
            )}
        </section>
    );
};

export default ProjectCarousel;
